/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {getDevice} from '@ion/core/src/services/Connect';
import {BaseAsset} from '@ion/core/src/types/entities';
import {DeviceFormFields} from '@ion/dls/src/editors/DeviceEditorUtils';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch} from '@ion/query/src/utils/helpers';
import {getDeviceEntityId} from '@/store/helpers';

/**
 * The Custom hook that finds the first associated device of a given asset that contains
 * the specified feature.
 * 
 * ```typescript
 *  import useAssociatedDevice from './useAssociatedDevice';
 *
 *  const device = useAssociatedDevice(asset, DeviceFormFields.ATTACHMENTS);
 *
 * ```
 * - Resolves all associated devices in parallel.
 * - Filters out null or invalid devices.
 * - Returns the first matching device or `null` if none found.
 */
export const useAssociatedDevice = (asset: BaseAsset, featureId: DeviceFormFields) => {
  const [device, setDevice] = useState<any>(null);

  const getAssociatedDeviceByFeature = useCallback(async(asset: BaseAsset, featureId: DeviceFormFields) => {
    const associatedDevices = asset?.devices ?? [];
    if (!associatedDevices.length) {
      return null;
    }
    
    const fetchedDevices = await Promise.all(
      associatedDevices.map(async(d) => {
        // only a device with a valid id is fetched - never `/devices/undefined` ...
        const deviceId = getDeviceEntityId(d);
        const devices  = deviceId ? await cacheOnFetch({
          queryKey : createKey(QueryKey.DEVICE_GET, deviceId),
          queryFn  : async() => await getDevice(deviceId)
        }) : null;
        return devices?.[0] ?? null;
      })
    );
    
    const validDevices = fetchedDevices.filter(Boolean);
    const foundDevice  = validDevices?.find(({features}) => features?.[featureId]) ?? null;
      
    setDevice(foundDevice);
  }, [setDevice]);

  useEffect(() => {
    if (asset && featureId) {
      getAssociatedDeviceByFeature(asset, featureId);
    }
  }, [asset, featureId, getAssociatedDeviceByFeature]);

  return device;
};

export default useAssociatedDevice;
